import { QueryKeys } from "@/constants/Query";
import axiosInstance from "@/lib/axios";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useDebounce } from "react-use";

interface ChartingConditionSearchParams {
  patientId: string;
  searchTerm?: string;
  motherEncounterId?: string;
  enabled?: boolean;
}

export interface ChartingCondition {
  conditionSystem: string;
  conditionCode: string;
  conditionDisplay: string;
  existingEpisodeOfCareId?: string;
  existingConditionId?: string;
}

/**
 * Search for SNOMED codes by ECL expression and search term
 * @param searchTerm - The search term to search for
 * @param eclExpression - The ECL expression to search for. Examples:
 * - << 404684003 - Children of parent code 404684003
 * - << 248326004 OR 364712009 - Children of parent code 248326004 or 364712009
 * @param enabled - Whether to enable the query
 * @returns The SNOMED codes
 */
export const useChartingConditionSearch = ({
  patientId,
  searchTerm,
  motherEncounterId,
  enabled = true,
}: ChartingConditionSearchParams) => {
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  useDebounce(
    () => {
      setDebouncedSearchTerm(searchTerm);
    },
    500,
    [searchTerm],
  );
  const query = useQuery<ChartingCondition[]>({
    queryFn: async () => {
      const response = await axiosInstance.get(
        "/charting/encounter-charting/conditions",
        {
          params: {
            motherEncounterId,
            searchTerm: debouncedSearchTerm,
          },
        },
      );

      return response.data?.results || [];
    },
    queryKey: QueryKeys.Patient.Chart.Conditions.Search.DEFAULT({
      patientId,
      searchTerm: debouncedSearchTerm as string,
      motherEncounterId: motherEncounterId as string,
    }),
    enabled:
      enabled &&
      Boolean(
        motherEncounterId &&
          debouncedSearchTerm &&
          debouncedSearchTerm.length > 2,
      ),
    staleTime: Number.POSITIVE_INFINITY,
  });

  return {
    ...query,
    isDebouncing: searchTerm !== debouncedSearchTerm,
  };
};
